/** @format */

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Alert, { AlertColor } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Slide, { SlideProps } from '@mui/material/Slide';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { FeedbackDto } from '../models/requests/FeedbackDto';

interface Message {
  content: string;
  severity: AlertColor;
  id: number;
  open: boolean;
}

interface Props {
  feedback: FeedbackDto;
  autoHideDuration?: number;
}

function SlideUp(props: SlideProps) {
  return <Slide {...props} direction='up' />;
}

const ToastAlert = styled(Alert)(({ theme }) => ({
  width: '100%',
  minWidth: theme.breakpoints.values.sm
    ? Math.min(400, theme.breakpoints.values.sm - 32)
    : 280,
  maxWidth: 440,
  borderRadius: theme.shape.borderRadius * 1.5,
  alignItems: 'flex-start',
  padding: theme.spacing(1.25, 2),
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 10px 40px rgba(0,0,0,0.45)'
      : '0 10px 40px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
  border: '1px solid',
  borderColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(15, 23, 42, 0.06)',
  backdropFilter: 'blur(8px)',
  '& .MuiAlert-message': {
    padding: theme.spacing(0.25, 0, 0, 0),
    width: '100%',
  },
  '& .MuiAlert-icon': {
    marginRight: theme.spacing(1.5),
    padding: 0,
    fontSize: 22,
    opacity: 0.95,
  },
  '& .MuiAlert-action': {
    marginRight: 0,
    paddingTop: 0,
    alignSelf: 'flex-start',
  },
}));

const severityIcons = {
  success: <CheckCircleOutlineIcon fontSize='inherit' />,
  error: <ErrorOutlineIcon fontSize='inherit' />,
  warning: <WarningAmberOutlinedIcon fontSize='inherit' />,
  info: <InfoOutlinedIcon fontSize='inherit' />,
};

export default function MessageResponse({
  feedback,
  autoHideDuration = 6500,
}: Props) {
  const theme = useTheme();
  const vertical = 'bottom';
  const horizontal = 'right';
  const [messages, setMessages] = React.useState<Message[]>([]);

  React.useEffect(() => {
    if (feedback.open && feedback.validation?.messages?.length) {
      const rawSeverity = feedback.severity;
      const severity: AlertColor =
        rawSeverity === 'success' ||
        rawSeverity === 'error' ||
        rawSeverity === 'warning' ||
        rawSeverity === 'info'
          ? rawSeverity
          : 'info';

      const newMessages = feedback.validation.messages.map(
        (content, index) => ({
          content,
          severity,
          id: Date.now() + index,
          open: true,
        })
      );

      setMessages((prev) => [...prev, ...newMessages]);
    }
  }, [feedback]);

  const handleClose = (id: number) => {
    return (event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, open: false } : msg))
      );
    };
  };

  React.useEffect(() => {
    if (messages.some((msg) => !msg.open)) {
      const timer = setTimeout(() => {
        setMessages((prev) => prev.filter((msg) => msg.open));
      }, 380);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  const openMessages = messages.filter((m) => m.open);
  const stackIndex = (id: number) =>
    openMessages.findIndex((m) => m.id === id);

  const baseBottomPx = parseFloat(String(theme.spacing(2)).replace(/px$/, '')) || 16;
  const stackStepPx = theme.palette.mode === 'dark' ? 76 : 72;

  return (
    <>
      {messages.map((message) => {
        const idx = stackIndex(message.id);
        const bottomOffset =
          baseBottomPx + (idx >= 0 ? idx * stackStepPx : 0);

        return (
          <Snackbar
            key={message.id}
            open={message.open}
            autoHideDuration={autoHideDuration}
            onClose={handleClose(message.id)}
            anchorOrigin={{ vertical, horizontal }}
            TransitionComponent={SlideUp}
            sx={{
              bottom: `${bottomOffset}px !important`,
              right: { xs: 16, sm: 24 },
              left: 'auto !important',
              maxWidth: 'calc(100vw - 32px)',
            }}>
            <ToastAlert
              severity={message.severity}
              variant='standard'
              iconMapping={severityIcons}
              onClose={handleClose(message.id)}>
              <Typography
                component='span'
                variant='body2'
                sx={{
                  fontWeight: 500,
                  lineHeight: 1.45,
                  letterSpacing: '0.01em',
                }}>
                {message.content}
              </Typography>
            </ToastAlert>
          </Snackbar>
        );
      })}
    </>
  );
}
